import {
  Component,
  inject,
  ViewChild,
  ChangeDetectionStrategy,
  forwardRef,
} from '@angular/core';

import { Store } from '@ngrx/store';
import { TemplatePortal, CdkPortalOutlet } from '@angular/cdk/portal';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { faGear, faFolderPlus } from '@fortawesome/free-solid-svg-icons';

import { getIconForDataObject } from 'src/app/shared/utils/object-utils';
import { PortalRegistryService } from 'src/app/core/services/portal-registry.service';

import { Role } from 'src/app/admin/admin.models';
import { NomObjectClass } from 'src/app/core/constants';

import {
  resetPrincipalsState,
  clearTemporaryChanges,
} from 'src/app/core/state/principals.actions';

import { AppState } from 'src/app/core/state/app.state';
import { FormEditorDialogBase } from 'src/app/shared/abstract/form-editor-dialog-base';
import {
  SuiDialogConfig,
  SuiDialogTabsComponent,
  SuiDialogLeftHeaderSlot,
  SuiDialogLeftContentSlot,
  SuiDialogRightHeaderSlot,
  SuiDialogRightContentSlot,
  SuiDialogRightFooterSlot,
} from '@sui/dialog';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { SuiButtonComponent } from '@sui/button';
import { EditorRoleComponent } from '../../pages/editor-role/editor-role.component';

interface DialogData {
  role: Role | null;
}
interface DialogOutput {}

@Component({
  selector: 'app-editor-role-dialog',
  templateUrl: './editor-role-dialog.component.html',
  styleUrl: './editor-role-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SuiDialogTabsComponent,
    SuiDialogLeftHeaderSlot,
    FaIconComponent,
    SuiDialogLeftContentSlot,
    CdkPortalOutlet,
    SuiDialogRightHeaderSlot,
    SuiButtonComponent,
    SuiDialogRightContentSlot,
    SuiDialogRightFooterSlot,
    forwardRef(() => EditorRoleComponent),
    TranslatePipe,
  ],
})
export class EditorRoleDialogComponent extends FormEditorDialogBase<
  any,
  DialogData,
  DialogOutput
> {
  static dialogConfig: Partial<SuiDialogConfig> = {
    width: '70vw',
    minWidth: 800,
    maxWidth: 1020,
  };
  @ViewChild('editorRole')
  protected override editorComponent!: any;

  private store = inject(Store<AppState>);
  private translateService = inject(TranslateService);
  private portalRegistry: PortalRegistryService = inject(PortalRegistryService);

  public tabsPortal!: TemplatePortal;
  public formPortal!: TemplatePortal;

  actionIcon = faGear;
  actionTitle = this.translateService.instant(
    'popup.action-title.create-new-role',
  );
  objectIcon = getIconForDataObject(NomObjectClass.ROLE);
  objectTitle = '';

  get role() {
    return this.dialogData.role as Role | null;
  }

  setupUiState(): void {
    if (this.role) {
      this.objectTitle = this.role.title;
      this.actionTitle = this.translateService.instant(
        'popup.action-title.edit-role-settings',
      );
    } else {
      this.actionIcon = faFolderPlus;
      this.objectTitle = this.translateService.instant(
        'popup.object-title.untitled',
      );
    }
  }

  ngOnInit(): void {
    this.setupUiState();
  }

  ngAfterViewInit(): void {
    this.tabsPortal = this.portalRegistry.get('general-form-editor-tabs');
    this.formPortal = this.portalRegistry.get('general-form-editor-form');

    // ! Setup dirty-close protection
    this.initializeFormEditorProtection();
  }

  ngOnDestroy(): void {
    // ! Clear temporary changes before resetting principals state
    this.store.dispatch(clearTemporaryChanges());
    this.store.dispatch(resetPrincipalsState());

    this.portalRegistry.clearAll();
  }

  async cancelAction(): Promise<void> {
    await this.cancel();
  }
}
